/* ──────────────────────────────────────────────────────────────
   Website — "Coming soon" preview.
   NOTE: The full Website implementation is preserved (commented
   out) below this component. Nothing was removed — to restore the
   old screen, delete this component and uncomment the block below.
   ────────────────────────────────────────────────────────────── */

import React from 'react';
import { View, Text, ScrollView, Platform, ViewStyle } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Icon } from '../../components/ui';

const FEATURES = [
  'Drag-and-drop design — zero code required',
  'Lightning-fast, SEO-ready pages out of the box',
  'Go live and share your catalog in a single click',
];

export default function WebsiteScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows, font } = theme;

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.paper }}
      contentContainerStyle={{ padding: 24, paddingBottom: 64, flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}
    >
      <View
        style={{
          width: '100%',
          maxWidth: 560,
          backgroundColor: colors.card,
          borderWidth: 1,
          borderColor: colors.line,
          borderRadius: radii.r4,
          paddingVertical: 40,
          paddingHorizontal: 32,
          alignItems: 'center',
          overflow: 'hidden',
          ...(shadows.lg as ViewStyle),
        }}
      >
        {/* ambient glow */}
        <View
          // @ts-ignore web radial glow
          style={Platform.OS === 'web'
            ? ({ position: 'absolute', top: -70, width: 300, height: 220, borderRadius: 150, background: `radial-gradient(circle, ${colors.info}26, transparent 70%)` } as any)
            : { position: 'absolute', top: -70, width: 300, height: 220, borderRadius: 150, backgroundColor: colors.infoSoft }}
        />

        {/* icon ring */}
        <View
          // @ts-ignore web gradient chip
          style={{
            width: 68, height: 68, borderRadius: 20, alignItems: 'center', justifyContent: 'center',
            borderWidth: 1, borderColor: colors.infoLine, marginBottom: 18,
            ...Platform.select({ web: { background: `linear-gradient(135deg, ${colors.info}33, ${colors.info}14)` } as any, default: { backgroundColor: colors.infoSoft } }),
          }}
        >
          <Icon name="globe" size={28} color={colors.info} />
        </View>

        {/* coming-soon badge */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 5, paddingHorizontal: 12, borderRadius: 999, backgroundColor: colors.infoSoft, borderWidth: 1, borderColor: colors.infoLine, marginBottom: 16 }}>
          <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.info }} />
          <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.info, fontFamily: font.sans, letterSpacing: 0.5 }}>COMING SOON</Text>
        </View>

        <Text style={{ fontSize: fontSize['2xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans, textAlign: 'center', letterSpacing: -0.4 }}>
          Your storefront, beautifully built 🌐
        </Text>
        <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: font.sans, textAlign: 'center', marginTop: 10, lineHeight: 22, maxWidth: 430 }}>
          A stunning, no-code website to showcase your catalog to the world is on its way. Launch a gorgeous storefront in minutes — no developers, no hassle, all you.
        </Text>

        <View style={{ gap: 12, marginTop: 24, maxWidth: 380, width: '100%' }}>
          {FEATURES.map((f) => (
            <View key={f} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Icon name="check-circle" size={16} color={colors.success} />
              <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.ink2, fontFamily: font.sans }}>{f}</Text>
            </View>
          ))}
        </View>

        <Text style={{ fontSize: fontSize.xs, color: colors.ink4, fontFamily: font.sans, textAlign: 'center', marginTop: 26 }}>
          Your brand deserves the spotlight — and it's coming to SK Catalog soon. 🚀
        </Text>
      </View>
    </ScrollView>
  );
}


// ════════════════════════════════════════════════════════════════
// ORIGINAL WEBSITE IMPLEMENTATION — preserved below, fully commented out.
// Nothing was removed. To restore: delete the preview component above
// and uncomment this block.
// ════════════════════════════════════════════════════════════════

// /* ──────────────────────────────────────────────────────────────
//    Distribution/Website/index.tsx
//    React Native + TypeScript — mirrors WebsiteView exactly
//    ────────────────────────────────────────────────────────────── */
// 
// import React, { useState } from 'react';
// import {
//   View,
//   Text,
//   ScrollView,
//   TouchableOpacity,
//   Platform,
//   useWindowDimensions,
//   ViewStyle,
//   TextStyle,
// } from 'react-native';
// 
// import { useTheme } from '../../context/themeContext';
// import { useNavigation } from '@react-navigation/native';
// import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
// import type { AppStackParamList } from '../../navigation/types';
// import { SK } from '../../data/mockData';
// import {
//   Icon,
//   Button,
//   Input,
//   Select,
//   Switch,
//   PageHeader,
//   useToast,
// } from '../../components/ui';
// import type { SelectOption } from '../../components/select';
// 
// // ── SectionCard ───────────────────────────────────────────────
// 
// interface SectionCardProps {
//   children: React.ReactNode;
//   style?: ViewStyle;
// }
// 
// function SectionCard({ children, style }: SectionCardProps) {
//   const { theme } = useTheme();
//   const { colors, radii, shadows } = theme;
//   return (
//     <View
//       style={[
//         {
//           backgroundColor: colors.card,
//           borderWidth: 1,
//           borderColor: colors.line,
//           borderRadius: radii.r3,
//           marginBottom: 16,
//           ...(shadows.sm as ViewStyle),
//         },
//         style,
//       ]}
//     >
//       {children}
//     </View>
//   );
// }
// 
// // ── SectionHead ───────────────────────────────────────────────
// 
// interface SectionHeadProps {
//   title: string;
//   icon?: string;
//   action?: React.ReactNode;
// }
// 
// function SectionHead({ title, icon, action }: SectionHeadProps) {
//   const { theme } = useTheme();
//   const { colors, fontSize, fontWeight } = theme;
//   return (
//     <View
//       style={{
//         paddingHorizontal: 16,
//         paddingVertical: 13,
//         borderBottomWidth: 1,
//         borderBottomColor: colors.line,
//         flexDirection: 'row',
//         alignItems: 'center',
//         justifyContent: 'space-between',
//         gap: 8,
//       }}
//     >
//       <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
//         {icon && <Icon name={icon as any} size={15} color={colors.ink2} />}
//         <Text
//           style={{
//             fontSize: fontSize.md,
//             fontWeight: fontWeight.semibold,
//             color: colors.ink,
//             fontFamily: theme.font.sans,
//             includeFontPadding: false,
//           }}
//         >
//           {title}
//         </Text>
//       </View>
//       {action}
//     </View>
//   );
// }
// 
// // ── SectionBody ───────────────────────────────────────────────
// 
// function SectionBody({ children, style }: { children: React.ReactNode; style?: ViewStyle }) {
//   return <View style={[{ padding: 16 }, style]}>{children}</View>;
// }
// 
// // ── FieldRow (shelf row) ──────────────────────────────────────
// 
// interface ShelfRowProps {
//   name: string;
//   count: number;
// }
// 
// function ShelfRow({ name, count }: ShelfRowProps) {
//   const { theme } = useTheme();
//   const { colors, fontSize, fontWeight } = theme;
//   return (
//     <View
//       style={{
//         flexDirection: 'row',
//         alignItems: 'center',
//         gap: 12,
//         paddingVertical: 11,
//         paddingHorizontal: 14,
//         borderWidth: 1,
//         borderColor: colors.line,
//         borderRadius: theme.radii.r2,
//         backgroundColor: colors.card,
//         marginBottom: 8,
//         ...(Platform.select({ web: { cursor: 'default' } as unknown as ViewStyle }) ?? {}),
//       }}
//     >
//       {/* grip icon */}
//       <View style={{ opacity: 0.4 }}>
//         <Icon name="grip" size={15} color={colors.ink3} />
//       </View>
//       {/* grid icon muted */}
//       <Icon name="grid" size={15} color={colors.ink3} />
//       {/* field-info */}
//       <View style={{ flex: 1, minWidth: 0 }}>
//         <Text
//           style={{
//             fontWeight: fontWeight.medium,
//             fontSize: fontSize.md,
//             color: colors.ink,
//             fontFamily: theme.font.sans,
//             includeFontPadding: false,
//           }}
//         >
//           {name}
//         </Text>
//         <Text
//           style={{
//             fontFamily: theme.font.mono,
//             fontSize: fontSize.xs,
//             color: colors.ink3,
//             includeFontPadding: false,
//           }}
//         >
//           Collection · {count} items
//         </Text>
//       </View>
//       <Switch checked={true} onChange={() => {}} />
//     </View>
//   );
// }
// 
// // ── KV row ────────────────────────────────────────────────────
// 
// function KvRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
//   const { theme } = useTheme();
//   const { colors, fontSize } = theme;
//   return (
//     <View
//       style={{
//         flexDirection: 'row',
//         justifyContent: 'space-between',
//         alignItems: 'center',
//         marginBottom: 6,
//       }}
//     >
//       <Text
//         style={{
//           fontSize: fontSize.sm,
//           color: colors.ink3,
//           fontFamily: theme.font.sans,
//           includeFontPadding: false,
//         }}
//       >
//         {label}
//       </Text>
//       <Text
//         style={{
//           fontSize: fontSize.sm,
//           color: colors.ink,
//           fontFamily: mono ? theme.font.mono : theme.font.sans,
//           includeFontPadding: false,
//         }}
//       >
//         {value}
//       </Text>
//     </View>
//   );
// }
// 
// // ── Divider ───────────────────────────────────────────────────
// 
// function Divider() {
//   const { theme } = useTheme();
//   return (
//     <View
//       style={{
//         height: 1,
//         backgroundColor: theme.colors.line,
//         marginVertical: 12,
//       }}
//     />
//   );
// }
// 
// // ── Main screen ───────────────────────────────────────────────
// 
// export default function WebsiteScreen() {
//   const { theme } = useTheme();
//   const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();
//   const toast = useToast();
//   const { colors, fontSize, fontWeight, radii } = theme;
//   const { width: winW } = useWindowDimensions();
//   const isMobile = winW < 820;
// 
//   const [live, setLive] = useState(true);
// 
//   const swatchColors = ['#3b2b1a', '#0b5d3b', '#7a1f2b', '#1e3a8a'];
//   const shelves = [
//     { name: 'Bestsellers', count: 12 },
//     { name: 'Festive Specials', count: 8 },
//     { name: 'New this season', count: 5 },
//   ];
// 
//   return (
//     <ScrollView
//       style={{ flex: 1, backgroundColor: colors.paper }}
//       contentContainerStyle={{ padding: isMobile ? 16 : 24, paddingBottom: 64 }}
//       showsVerticalScrollIndicator={false}
//     >
//       {/* max-width wrapper */}
//       <View style={{ maxWidth: 980, alignSelf: 'center', width: '100%' }}>
//         {/* Page header row */}
//         <View
//           style={{
//             flexDirection: 'row',
//             alignItems: 'flex-start',
//             justifyContent: 'space-between',
//             flexWrap: 'wrap',
//             gap: 16,
//             marginBottom: 20,
//           }}
//         >
//           <View style={{ flex: 1, minWidth: 240 }}>
//             <Text
//               style={{
//                 fontSize: fontSize['3xl'],
//                 fontWeight: fontWeight.semibold,
//                 color: colors.ink,
//                 letterSpacing: -0.02 * fontSize['3xl'],
//                 fontFamily: theme.font.sans,
//                 includeFontPadding: false,
//               }}
//             >
//               Website
//             </Text>
//             <Text
//               style={{
//                 fontSize: fontSize.md,
//                 color: colors.ink3,
//                 marginTop: 4,
//                 fontFamily: theme.font.sans,
//                 includeFontPadding: false,
//               }}
//             >
//               Your bundled public catalog — the first consumer of the API. No checkout; enquiries hand off to CRM.
//             </Text>
//           </View>
//           <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
//             <Button
//               icon="external"
//               iconRight
//               onPress={() => navigation.navigate('site')}
//             >
//               Open preview
//             </Button>
//             <Button
//               kind="accent"
//               icon="check"
//               onPress={() => toast.success('Published website settings')}
//             >
//               Publish changes
//             </Button>
//           </View>
//         </View>
// 
//         {/* editor-grid: wide screens side-by-side, narrow screens stacked */}
//         <View
//           style={
//             Platform.OS === 'web' && !isMobile
//               ? ({
//                   display: 'grid',
//                   gridTemplateColumns: '1fr 320px',
//                   gap: 20,
//                   alignItems: 'start',
//                 } as any)
//               : { flexDirection: 'column', gap: 20 }
//           }
//         >
//           {/* Main column */}
//           <View style={Platform.OS !== 'web' ? {} : undefined}>
//             {/* General section */}
//             <SectionCard>
//               <SectionHead title="General" icon="globe" />
//               <SectionBody>
//                 <View style={{ gap: 12 }}>
//                   <Input label="Site title" placeholder="Your store name" />
//                   <Input label="Tagline" placeholder="A short tagline" />
//                   <Input
//                     label="Domain"
//                     placeholder="shop.yourdomain.com"
//                   />
//                   {/* form-grid */}
//                   <View
//                     style={
//                       Platform.OS === 'web'
//                         ? ({
//                             display: 'grid',
//                             gridTemplateColumns: '1fr 1fr',
//                             gap: 14,
//                           } as any)
//                         : { gap: 12 }
//                     }
//                   >
//                     <Select
//                       label="Default language"
//                       options={SK.languages.map((l): SelectOption => ({ label: l.label, value: l.label }))}
//                     />
//                     <Select
//                       label="Default location"
//                       options={SK.locations
//                         .filter((l) => l.id !== 'all')
//                         .map((l): SelectOption => ({ label: l.name, value: l.id }))}
//                     />
//                   </View>
//                 </View>
//               </SectionBody>
//             </SectionCard>
// 
//             {/* Homepage shelves section */}
//             <SectionCard>
//               <SectionHead
//                 title="Homepage shelves"
//                 icon="grid"
//                 action={
//                   <Button
//                     size="sm"
//                     icon="plus"
//                     onPress={() => toast.info('Add shelf (demo)')}
//                   >
//                     Add shelf
//                   </Button>
//                 }
//               />
//               <SectionBody>
//                 {shelves.map((s) => (
//                   <ShelfRow key={s.name} name={s.name} count={s.count} />
//                 ))}
//               </SectionBody>
//             </SectionCard>
//           </View>
// 
//           {/* Sidebar */}
//           <View
//             style={
//               Platform.OS === 'web'
//                 ? ({ position: 'sticky', top: 0, gap: 14 } as any)
//                 : { gap: 14 }
//             }
//           >
//             {/* Status section */}
//             <SectionCard style={{ marginBottom: 0 }}>
//               <SectionHead title="Status" />
//               <SectionBody>
//                 {/* t-spread */}
//                 <View
//                   style={{
//                     flexDirection: 'row',
//                     alignItems: 'center',
//                     justifyContent: 'space-between',
//                   }}
//                 >
//                   <View>
//                     <Text
//                       style={{
//                         fontWeight: fontWeight.medium,
//                         fontSize: fontSize.md,
//                         color: colors.ink,
//                         fontFamily: theme.font.sans,
//                         includeFontPadding: false,
//                       }}
//                     >
//                       {live ? 'Live' : 'Offline'}
//                     </Text>
//                     <Text
//                       style={{
//                         fontSize: fontSize.xs,
//                         color: colors.ink3,
//                         fontFamily: theme.font.sans,
//                         includeFontPadding: false,
//                       }}
//                     >
//                       shop.madrasfilter.co
//                     </Text>
//                   </View>
//                   <Switch
//                     checked={live}
//                     onChange={(checked) => {
//                       setLive(checked);
//                       toast.info(checked ? 'Site is live' : 'Site taken offline');
//                     }}
//                   />
//                 </View>
// 
//                 <Divider />
// 
//                 {/* KV grid */}
//                 <KvRow label="Published items" value="41" mono />
//                 <KvRow label="Indexed (search)" value="41" mono />
//                 <KvRow label="Last build" value="3m ago" />
//               </SectionBody>
//             </SectionCard>
// 
//             {/* Theme section */}
//             <SectionCard style={{ marginBottom: 0 }}>
//               <SectionHead title="Theme" />
//               <SectionBody>
//                 <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
//                   {swatchColors.map((c) => (
//                     <TouchableOpacity
//                       key={c}
//                       onPress={() => toast.info('Theme colour set')}
//                       style={{
//                         width: 30,
//                         height: 30,
//                         borderRadius: radii.r2,
//                         backgroundColor: c,
//                         borderWidth: 2,
//                         borderColor: colors.card,
//                         shadowColor: colors.line,
//                         shadowOffset: { width: 0, height: 0 },
//                         shadowOpacity: 1,
//                         shadowRadius: 0,
//                         ...(Platform.select({
//                           web: { outline: `1px solid ${colors.line}`, cursor: 'pointer' } as any,
//                         }) ?? {}),
//                       }}
//                     />
//                   ))}
//                 </View>
//               </SectionBody>
//             </SectionCard>
//           </View>
//         </View>
//       </View>
//     </ScrollView>
//   );
// }
